import { Users, Eye, Server, Bot, UserX, LayoutGrid } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { AppUsageStats } from '../../types/appUsage';
import { fmtNum, fmtChf } from './format';

interface Props {
    stats: AppUsageStats | null;
}

interface Tile {
    label: string;
    value: string;
    sub?: string;
    icon: LucideIcon;
    color: string;
}

/** A figure that does not exist for the selected app is "—", never 0. */
const num = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : fmtNum(n));

/**
 * Six KPI tiles. Deliberately NOT clickable — they honor only the window and
 * app scope (house rule: tile numbers never move when the tables are filtered).
 */
export function AppUsageStatsBar({ stats }: Props) {
    const tiles: Tile[] = [
        {
            label: 'Active users', icon: Users, color: 'text-green-600',
            value: stats ? fmtNum(stats.activeUsers) : '—',
            sub: stats?.entitledUsers != null ? `of ${fmtNum(stats.entitledUsers)} entitled` : undefined,
        },
        {
            label: 'Page views', icon: Eye, color: 'text-blue-600',
            value: stats ? fmtNum(stats.pageViews) : '—',
            sub: stats ? `${fmtNum(stats.sessions)} sessions` : undefined,
        },
        {
            label: 'API calls', icon: Server, color: 'text-gray-600',
            value: stats ? fmtNum(stats.apiCalls) : '—',
            sub: stats ? `${fmtNum(stats.featureEvents)} feature events` : undefined,
        },
        {
            label: 'AI cost (CHF)', icon: Bot, color: 'text-purple-600',
            value: stats?.aiCostChf != null ? fmtChf(stats.aiCostChf) : '—',
            sub: stats ? (stats.aiCalls != null ? `${fmtNum(stats.aiCalls)} AI calls` : 'not logged for this app') : undefined,
        },
        {
            label: 'Never used', icon: UserX, color: 'text-red-600',
            value: num(stats?.neverUsedUsers),
            sub: 'entitled users, 0 events',
        },
        {
            label: 'Stale pages', icon: LayoutGrid, color: 'text-amber-600',
            value: num(stats?.stalePages),
            sub: stats?.totalPages != null ? `of ${fmtNum(stats.totalPages)} pages` : undefined,
        },
    ];

    return (
        <div className="space-y-1">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {tiles.map(t => {
                    const Icon = t.icon;
                    return (
                        <div key={t.label} className="w-full rounded-lg border border-gray-200 bg-white p-3">
                            <div className="flex items-center gap-2">
                                <Icon className={`h-4 w-4 ${t.color}`} />
                                <span className="text-xs font-medium text-gray-500">{t.label}</span>
                            </div>
                            <p className="mt-1 text-xl font-bold text-gray-900">{t.value}</p>
                            {t.sub && <p className="text-xs text-gray-500">{t.sub}</p>}
                        </div>
                    );
                })}
            </div>
            {stats && stats.entitledUsers === null && (
                <p className="text-xs text-gray-500">
                    Entitlement, adoption, never-used and stale-page figures exist only for the Reporting App:
                    the other apps grant access through Entra groups, which this page does not see.
                </p>
            )}
        </div>
    );
}
